
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  Ref,
  ref,
  watch
} from "vue";
import { wrapLoading } from "../../utils/wrap_loading";
import { dispatchError } from "../../utils/error_handling";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { ContentComment, Course } from "../../../../domain/entities/entities";
import { getOrderFromURL } from "../../utils/order_utils";
import Card from "../../components/application/dashboard/Card.vue";
import Pagination from "../../components/application/shop/PaginationDefault.vue";
import {
  updateQueries,
  updateQuery
} from "../../../repository/utils/routing_utils";
import Search from "../../components/application/inputs/Search.vue";
import { search } from "../../components/application/data-table/table-order-utils";
import VideoPlayButton from "../../components/application/buttons/VideoPlay.vue";
import ButtonComment from "../../components/application/DashboardComments/ButtonComment.vue";
import {
  disableBodyOverflow,
  enableBodyOverflow,
  formattedDuration,
  isDecolaTemplate,
  toLocaleDateTime
} from "../../components/mixins";
import ModalIntroductoryContent from "../../components/application/ModalIntroductoryContent.vue";
import ContentFallback from "../../components/application/moldure/ContentFallback.vue";
import Comment from "../../components/application/DashboardComments/Comment.vue";
import CommentsModal from "../../components/application/DashboardComments/CommentsModal.vue";

const __default__ = defineComponent({
  name: "InterestTraining",
  components: {
    CommentsModal,
    Comment,
    ContentFallback,
    ModalIntroductoryContent,
    ButtonComment,
    VideoPlayButton,
    Search,
    Pagination,
    Card
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const selectedStatus = ref("0");

    const course = ref(new Course());

    const commentModalTitle = ref("");

    /** Boolean used to tell when a click event has been fired
     * from below the `div.card` in the DOM tree. */
    let hasLowLevelActions = false;

    const isCommentsModalOpen: Ref<boolean> = ref(false);

    const openModalIntroductoryContent = ref(false);

    const comments: ComputedRef<ContentComment[]> = computed(
      () => store.getters["course_module/comments"]
    );

    const trainings: ComputedRef<Course[]> = computed(
      () => store.getters["interest_module/trainingsInterests"]
    );

    const numberOfPages: ComputedRef<number> = computed(() => {
      return store.getters["interest_module/numberOfPagesTrainings"];
    });

    const interestImageURL = computed(() => {
      const interests = store.getters["interest_module/userInterests"] || [];
      const id = Number(route.params.interest_id);
      return interests.find(i => i.id === id)?.imageURL || "";
    });

    onBeforeMount(async () => {
      await loadUserInterests();
      await listTrainingsByInterest();
    });

    const interestTitle = computed(() => route.query.title || "Interesse");

    function openIntroductoryContent(_course: Course) {
      course.value = _course;
      openModalIntroductoryContent.value = true;
    }

    function closeModalIntroductoryContent() {
      openModalIntroductoryContent.value = false;
    }

    const openCommentsModal = async () => {
      disableBodyOverflow();
      isCommentsModalOpen.value = true;
    };

    /** Dispatch an action to open the comment modal on screen. */
    const closeCommentsModal = async () => {
      enableBodyOverflow();
      isCommentsModalOpen.value = false;
    };

    const getToday = (): string => {
      return new Date().toISOString().slice(0, 10);
    };

    const isNewCourse = (training: Course): boolean => {
      const today = getToday();
      if (training.releaseStartDate && training.releaseEndDate) {
        return (
          today >= training.releaseStartDate && today <= training.releaseEndDate
        );
      }
      return (
        today >= training.releaseStartDate || today <= training.releaseEndDate
      );
    };

    async function listTrainingsByInterest(): Promise<void> {
      try {
        const order = getOrderFromURL(route);
        await wrapLoading(async () => {
          await store.dispatch("interest_module/listTrainingsByInterest", {
            order,
            interestID: Number(route.params.interest_id)
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    async function loadUserInterests(): Promise<void> {
      try {
        await wrapLoading(async () => {
          await store.dispatch("interest_module/getUserInterests");
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    /** Dispatch an action to load the comments. */
    const loadContentCommentsById = async (id: number) => {
      const order = getOrderFromURL(route);

      await wrapLoading(async () => {
        await store.dispatch("course_module/loadContentCommentsById", {
          courseID: id,
          order: order
        });
      });
    };

    /** Display the modal with the comments about the course. */
    const loadCourseCommentsModal = async (course: Course) => {
      try {
        commentModalTitle.value = course.title;
        await updateQueries(router, {
          pageDefault: "1",
          courseID: course.id.toString()
        });
        await openCommentsModal();
        await loadContentCommentsById(course.id);
      } catch (e) {
        await dispatchError(e);
      }
    };

    watch(
      () => [route.query.status, route.query.search, route.query.page],
      async (newVal, oldVal) => {
        if (
          route.name === "interest" &&
          JSON.stringify(newVal) !== JSON.stringify(oldVal)
        ) {
          await wrapLoading(async () => {
            await listTrainingsByInterest();
          });
        }
      }
    );

    watch(
      () => route.query.pageDefault,
      async () => {
        if (
          isCommentsModalOpen.value &&
          route.query.pageDefault &&
          route.query.courseID
        ) {
          await loadContentCommentsById(
            parseInt(route.query.courseID.toString())
          );
        }
      }
    );

    const onStatusChange = () => {
      router.push({
        query: {
          ...route.query,
          status: selectedStatus.value,
          page: "1"
        }
      });
    };

    const goToTrainingView = async (courseID: number): Promise<void> => {
      if (hasLowLevelActions) {
        hasLowLevelActions = false;
        return;
      }
      await router.push({ name: "course", params: { course_id: courseID } });
    };

    return {
      selectedStatus,
      interestTitle,
      trainings,
      numberOfPages,
      comments,
      openModalIntroductoryContent,
      isCommentsModalOpen,
      commentModalTitle,
      course,
      interestImageURL,
      openIntroductoryContent,
      loadCourseCommentsModal,
      closeCommentsModal,
      closeModalIntroductoryContent,
      openCommentsModal,
      isNewCourse,
      onStatusChange,
      goToTrainingView,
      toLocaleDateTime,
      isDecolaTemplate,
      formattedDuration,
      search,
      updateQuery,
    };
  }
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "a72faaa4": (_ctx.backgroundImage)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__